import { useEffect, useMemo } from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { pageView } from '@belong/analytics';
import { IProductLoadRequest } from '@belong/common-nextjs-server-side-props';
import { getConfig } from '@belong/configs/next/config';
import { PUBLIC_PAGES } from '@belong/constants/src';
import { logger } from '@belong/logging/logger';
// import the SSP chaining utility and context type
import { offerContentManager } from '@belong/offers';
import useAnalyticsEvents from '@belong/providers/analyticsEvents';
import AnalyticsContext from '@belong/providers/analyticsEvents/Context';
import { useAnalyticsDebugger } from '@belong/providers/analyticsEvents/debug';
import useConnectToAnalyticsVendors from '@belong/react-hooks/useConnectToAnalyticsVendors';
import { composeAndRunSSPChain } from '@belong/ssp-execution';
import { PRODUCT_FAMILY } from '@belong/types/product';
import { withBasePath, withoutBasePath, withoutParams } from '@belong/url-utils';
// import helpers
// import the rendering components and type guards
import ArticleBlogView from '../components/templates/ArticleBlogView';
import PageUnauthenticatedView from '../components/templates/PageUnauthenticatedView';
import { isIArticleBlog } from '../contentful/factories/ArticleBlog';
import { getABTestAnalyticsData } from '../helpers/getABTestAnalyticsData';
import { getTechTypeForPostSq } from '../helpers/getTechTypeForPostSq';
import { withServerSideAppProps } from '../helpers/nextjs/withServerSideAppProps';
// import chaining functions
import { ROUTES } from '../helpers/routes';
import {
  adobeTargetSSP,
  alertDismissalSSP,
  blogArticlesSSP,
  fetchProductsAndOffers,
  getContentRetrievalSSP,
  getNbnCartStatusSSP,
  promoCodeSSP,
  schemaDataSSP
} from '../ssp-chain';
import { TPublicSSPContext } from '../ssp-chain/types';
import type { IDynamicPageProps, IPageData, IServerSidePageProps, PageServerSidePropsFunction } from '../core/types';

// dynamic imports for conditionally rendered components
const ErrorPage = dynamic(() => import('./_error'));

export default function DynamicPage({ pageData }: IServerSidePageProps<IDynamicPageProps>) {
  const analyticsEvents = useAnalyticsEvents();
  const config = useMemo(() => getConfig().publicRuntimeConfig, []);
  useConnectToAnalyticsVendors(analyticsEvents, config.analytic.shouldRun);
  useAnalyticsDebugger();

  const { query, asPath } = useRouter();
  const slug = query.slug as string[];
  const isSearchResultsPage = slug && slug[0] === withoutBasePath(PUBLIC_PAGES.SEARCH).replace(/\//g, '');
  const searchQuery = (isSearchResultsPage && (query.query as string)) || undefined;

  const {
    adobeTargetActivities,
    description,
    journeyName,
    keywords,
    noFollow,
    noIndex,
    pageTitle,
    pageUrl,
    sections: pageSections,
    statusCode,
    relatedArticles,
    offerContent
  } = pageData ?? {};

  const extraProps = useMemo(() => getTechTypeForPostSq(withBasePath(asPath)), [asPath]);

  if (offerContent) {
    offerContentManager.init(offerContent);
  }

  useEffect(() => {
    analyticsEvents.send({ ...pageView(pageData, config), ...extraProps });
  }, [analyticsEvents, pageData, config, extraProps]);

  useEffect(() => {
    // check if current url has anchor
    const { hash } = window.location;

    if (hash) {
      // time out to ensure page has rendered before scrolling
      setTimeout(() => {
        const anchor = document.getElementById(hash.split('#')[1]);
        if (anchor) {
          anchor.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
    }
  }, [asPath]);

  if (statusCode && statusCode >= 400) {
    return <ErrorPage statusCode={statusCode} />;
  }

  if (!pageData) {
    return <ErrorPage statusCode={500} />;
  }

  const sections: { contentType?: string; [key: string]: any }[] = pageSections ?? [];

  return (
    <>
      <AnalyticsContext
        value={{
          currentUrl: pageUrl,
          journeyName,
          experiments: getABTestAnalyticsData(adobeTargetActivities)
        }}
      />
      <Head>
        <title key="title">{`${searchQuery ? `"${searchQuery}" - ` : ''}${pageTitle}`}</title>
        <meta name="description" key="description" content={description} />
        <meta name="format-detection" content="telephone=no" />
        {keywords && keywords !== '' && <meta name="keywords" key="keywords" content={keywords} />}
        {noIndex && noFollow && <meta name="robots" content="noindex, nofollow" />}
        {noIndex && !noFollow && <meta name="robots" content="noindex" />}
        {!noIndex && noFollow && <meta name="robots" content="nofollow" />}
      </Head>
      {isIArticleBlog(pageData) ? (
        <ArticleBlogView page={pageData} relatedArticles={relatedArticles ?? []} />
      ) : (
        <PageUnauthenticatedView sections={sections} />
      )}
    </>
  );
}

export const getPageServerSideProps: PageServerSidePropsFunction<IDynamicPageProps> = async (
  context,
  appServerSideProps
) => {
  const { correlationId } = appServerSideProps;
  const { resolvedUrl } = context;
  const pageUrl = withoutParams(resolvedUrl);
  logger.debug(correlationId, `Served by public: ${pageUrl}`);
  // Compose SSP context
  const initialCtx: TPublicSSPContext = {
    context,
    appServerSideProps,
    pageProps: {} as IPageData,
    pagePath: withBasePath(pageUrl)
  };

  // Only load products + offers for the home page
  // All other pages that require products + offers should explicitly call fetchProductsAndOffers in their SSP chain
  let productsToLoad: IProductLoadRequest[] = [];
  if (pageUrl === ROUTES.HOME) {
    productsToLoad = [
      [PRODUCT_FAMILY.MOBILE_PLANS, true],
      [PRODUCT_FAMILY.MOBILE_DATA_PLANS, true]
    ];
  }

  // Compose and run SSP chain (status code, content, adobe target, blog/related articles, alert, schema data)
  // NOTE: Page Data is not available in the context until after the AdobeTarget SSP process
  const finalCtx = await composeAndRunSSPChain<TPublicSSPContext>([
    getContentRetrievalSSP(),
    adobeTargetSSP,
    fetchProductsAndOffers(...productsToLoad),
    promoCodeSSP,
    blogArticlesSSP,
    alertDismissalSSP,
    schemaDataSSP,
    getNbnCartStatusSSP
  ])(initialCtx);

  // The destination already carries the /go base path, so opt out of Next's basePath.
  if (finalCtx.redirectUrl) {
    return { redirect: { destination: finalCtx.redirectUrl, permanent: false, basePath: false } };
  }

  return {
    props: {
      swrInitialCache: finalCtx.appServerSideProps.swrInitialCache,
      pageData: {
        ...finalCtx.pageProps,
        statusCode: finalCtx.statusCode,
        blogArticle: finalCtx.blogArticle,
        relatedArticles: finalCtx.relatedArticles,
        adobeTargetActivities: finalCtx.adobeTargetActivities,
        alert: finalCtx.alert,
        schemaData: finalCtx.schemaData,
        isNbnOrderCartReady: finalCtx.isNbnOrderCartReady
      }
    }
  };
};

export const getServerSideProps = withServerSideAppProps(getPageServerSideProps);
